import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import axios from 'axios';
import { toast } from 'react-toastify';

import { userActions } from 'entities/User';
import { searchVersionPreferenceActions } from 'features/SearchVersionPreference/model/slice/SearchVersionPreferenceSlice';
import { isJestRuntime } from 'shared/const/envVariables';
import { USER_ACCESS_TOKEN } from 'shared/const/localstorage';
import { ORDER_VERSION_PREFERENCE_HEADER } from 'shared/const/orderVersionPreference';
import { SEARCH_VERSION_PREFERENCE_HEADER } from 'shared/const/searchVersionPreference';
import { convertKeysToCamelCase } from 'shared/lib/convertKeysToCamelCase/convertKeysToCamelCase';
import { convertKeysToSnakeCase } from 'shared/lib/convertKeysToSnakeCase/convertKeysToSnakeCase';
import { isCreateOrderRequest } from 'shared/lib/orderVersionPreference/isCreateOrderRequest';
import {
    getOrderVersionPreferenceHeaderValue,
    readOrderVersionPreferenceFromStorage,
} from 'shared/lib/orderVersionPreference/orderVersionPreferenceStorage';
import { getSearchVersionFromResponseHeaders } from 'shared/lib/searchVersionPreference/getSearchVersionFromResponseHeaders';
import { isCatalogSearchRequest } from 'shared/lib/searchVersionPreference/isCatalogSearchRequest';
import {
    getSearchVersionPreferenceHeaderValue,
    readSearchVersionPreferenceFromStorage,
} from 'shared/lib/searchVersionPreference/searchVersionPreferenceStorage';

import { resolveApiBaseUrl } from './resolveApiBaseUrl';
import { ExtendedAxiosRequestConfig } from './types';

import type { StateSchema } from 'app/providers/StoreProvider/config/StateSchema';

let store: ToolkitStore<StateSchema>;

export const injectStore = (_store: ToolkitStore<StateSchema>) => {
    store = _store;
};

const apiUrl = resolveApiBaseUrl();

export const $api = axios.create({
    baseURL: apiUrl,
});

$api.interceptors.request.use((config) => {
    const newConfig = { ...config };
    newConfig.url = config.url;

    if (newConfig.headers) {
        newConfig.headers.Authorization = `Bearer ${
            localStorage.getItem(USER_ACCESS_TOKEN) || ''
        }`;

        if (isCatalogSearchRequest(config)) {
            const preferenceHeaderValue = getSearchVersionPreferenceHeaderValue(
                readSearchVersionPreferenceFromStorage(),
            );

            if (preferenceHeaderValue) {
                newConfig.headers[SEARCH_VERSION_PREFERENCE_HEADER] =
                    preferenceHeaderValue;
            }
        }

        if (isCreateOrderRequest(config)) {
            const preferenceHeaderValue = getOrderVersionPreferenceHeaderValue(
                readOrderVersionPreferenceFromStorage(),
            );

            if (preferenceHeaderValue) {
                newConfig.headers[ORDER_VERSION_PREFERENCE_HEADER] =
                    preferenceHeaderValue;
            }
        }
    }

    if (config.data && !(config.data instanceof FormData)) {
        newConfig.data = convertKeysToSnakeCase(config.data);
    }

    if (config.params) {
        newConfig.params = convertKeysToSnakeCase(config.params);
    }

    return newConfig;
});

$api.interceptors.response.use(
    (response) => {
        if (response.data && !(response.data instanceof Blob)) {
            const config = response.config as ExtendedAxiosRequestConfig;
            const keysToNotConvert = config.keysToNotConvert || [];

            response.data = convertKeysToCamelCase(
                response.data,
                keysToNotConvert,
            );
        }

        if (
            !isJestRuntime &&
            store &&
            isCatalogSearchRequest(response.config)
        ) {
            const searchVersion = getSearchVersionFromResponseHeaders(
                response.headers,
            );

            if (searchVersion) {
                store.dispatch(
                    searchVersionPreferenceActions.setLastUsedVersion(
                        searchVersion,
                    ),
                );
            }
        }

        return response;
    },
    (error) => {
        if (error?.response?.status === 401) {
            if (!isJestRuntime) {
                store.dispatch(userActions.logout());
            }

            toast('Session expired');
        }
        return Promise.reject(error);
    },
);
